import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ListTableComponent, PopupDialogComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../../shared/modules/ng-zorro/ng-zorro.module';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { TableRowActions } from '../../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../../shared/interfaces/list.interface';
import { ListService } from '../../../../../shared/services/list.service';
import { InvoiceService } from '../../../../../services/invoice.service';
import { LookupService } from '../../../../../services/lookup.service';
import { UserService } from '../../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../../shared/utils/date-time-formatter.service';
import { ErrorAlertService } from '../../../../../shared/services/error-alert.service';
import { DialogConfig, DialogService } from '../../../../../shared/services/dialog.service';
import { AllowListAuthStore } from '../../../../../shared/authorization/allow-list-auth.store';
import { PAGE_AVAILABLE_ACTIONS, PAGE_CONSTANTS } from '../../../../../shared/constants/page.constants';
import { InvoiceFilterComponent } from '../invoices-staff-list/invoice-filter/invoice-filter.component';

@Component({
  selector: 'ppl-invoice-goodtopay-list',
  standalone: true,
  imports: [
    ListTableComponent,
    NgZorroModule,
    CommonModule,
    InvoiceFilterComponent,
    PopupDialogComponent,
    FormsModule,
    NzFormDirective,
    ReactiveFormsModule
  ],
  templateUrl: './invoice-goodtopay-list.component.html',
  styleUrl: './invoice-goodtopay-list.component.css'
})
export class InvoiceGoodToPayListComponent {
  // Inherits the Staff Invoices page access (actions/buttons) until a dedicated page id exists in
  // the server-side allow-list config.
  // TODO: switch to PAGE_CONSTANTS.STAFF_GOODTOPAY_INVOICES then.
  pageId: string = PAGE_CONSTANTS.STAFF_INVOICES;

  /** Invoice_Business_Status code for "Good to pay". */
  private readonly GOOD_TO_PAY = 'GP';
  /** Invoice_Processing_Status code for "Ready for Payment". */
  private readonly READY_FOR_PAYMENT = 'RP';
  /** Status sent to the deny-invoice API. */
  private readonly DENIED = 'D';

  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(InvoiceFilterComponent) invoiceFilterChildComponent!: InvoiceFilterComponent;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;

  private dialogSvc = inject(DialogService);

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  appliedFilters: any;
  programName!: string;
  invoices: any[] = [];
  hasSearched = false;
  title: string = '';
  okText: string = '';
  showActionPopup: boolean = false;
  buttonActionEvent: any;
  selectedCheckboxes: any;
  invoiceActionForm!: FormGroup;
  businessStatusList: any;
  processingStatusList: any;

  columnsToBeHidden: string[] = [];
  actionsToBeHidden: string[] = [];
  isPageRestrictionLoaded = false;
  isReadOnly = false;

  cols = [
    {
      field: 'payPeriod',
      header: 'Pay \nPeriod',
      jsonpath: 'payPeriod',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceDate',
      header: 'Service \nDate',
      jsonpath: 'serviceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'invoiceDate',
      header: 'Invoice \nDate',
      jsonpath: 'invoiceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'invoiceNumber',
      header: 'Invoice \nNumber',
      jsonpath: 'invoiceNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'consumerName',
      header: 'Participant \nName',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'consumerPplId',
      header: 'Participant \nPPL ID',
      jsonpath: 'consumerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'Provider \nName',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'providerPplId',
      header: 'Provider \nPPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'businessStatus',
      header: 'Application \nStatus',
      jsonpath: 'businessStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processStatusLkp',
      header: 'Processing \nStatus',
      jsonpath: 'processStatusLkp',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceCodeDescription',
      header: 'Service \Code',
      jsonpath: 'serviceCodeDescription',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'planServiceCode',
      header: 'Service \Description',
      jsonpath: 'planServiceCode',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billedUnits',
      header: 'Units',
      jsonpath: 'billedUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'unitPrice',
      header: 'Rate',
      jsonpath: 'unitPrice',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalCost',
      header: 'Total \nCost',
      jsonpath: 'totalCost',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  // Good To Pay invoices are already cleared for payment — only Deny / Deny All apply.
  buttons = [
    {
      button_id: "Deny",
      button_label: "Deny",
      buttonAction: "denyInvoice",
      className: "primary-btn-color"
    },
    {
      button_id: "DenyAll",
      button_label: "Deny All",
      buttonAction: "denyAllInvoices",
      className: "primary-btn-color"
    },
  ]

  // Status and Processing Status are pinned to this page's scope: `disabled` keeps them visible but
  // unselectable, and the filter emits getRawValue() so the values still reach the search payload.
  fields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'processStatusLkp', show: true, default: this.READY_FOR_PAYMENT, disabled: true },
    { field: 'serviceCode', show: true },
    { field: 'status', show: true, default: this.GOOD_TO_PAY, disabled: true }
  ];

  // Filter control name → the jsonPath getFilters() sends it under. Used by isFilterValid to
  // confirm the search was actually run with the filters currently on screen.
  keyMapping: { [key: string]: string } = {
    Timeframe: "serviceDate",
    consumer: "consumerName",
    provider: "providerName",
    providerPplId: "providerPplId",
    consumerPplId: "consumerPplId",
    processStatusLkp: "processStatusLkp",
    status: "businessStatus"
  };

  constructor(
    private listService: ListService,
    private fb: FormBuilder,
    private lookupService: LookupService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private invoiceService: InvoiceService,
    private errorAlertService: ErrorAlertService,
    private userService: UserService,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    // Deny captures free-text remarks only — this page has no process codes.
    this.invoiceActionForm = this.fb.group({
      remarks: [null, [Validators.required, Validators.maxLength(10000)]],
    });

    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Invoice_Business_Status');
    this.processingStatusList = this.lookupService.getLookupValue('Invoice_Processing_Status');
    this.setPageAccess();
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    if (pageConfig) {
      const availableActions = PAGE_AVAILABLE_ACTIONS[this.pageId] ?? [];
      const allowedActions = this.allowListStore.getAllowedActions(this.pageId);
      this.actionsToBeHidden = availableActions.filter(
        action => !allowedActions.some(aa => aa.toLowerCase() === action.toLowerCase())
      );
      const allowedButtons = this.allowListStore.getAllowedButtons(this.pageId);
      this.isReadOnly = allowedActions.length === 0 && allowedButtons.length === 0;
    } else {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: any[], startingPageNumber = 1, recordsPerPage = 20) {
    if (searchParams.length == 0) {
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }

  searchInvoices(filters: any, startingPageNumber = 1, currentPage = 0, recordsPerPage = 20) {
    this.hasSearched = true;

    const processedPayload = this.buildRequestPayload(
      'invoice-unfiltered',
      'invoiceLineId',
      filters,
      startingPageNumber,
      recordsPerPage
    );

    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const records = this.processData(data.responsedata);
        if (startingPageNumber > 1) {
          this.invoices = [...this.invoices, ...records];
        } else {
          this.invoices = records;
        }
      });
  }

  processData(data: any) {
    if (data && data.length > 0) {
      data.forEach((element: any) => {
        element.payPeriod = element.payrollStartDate + ' - ' + element.payrollEndDate;
      });
    }

    return data;
  }

  loadRecordsAfterUpdate() {
    this.clearGridSelection();
    let filters = this.appliedFilters;
    if (!this.appliedFilters || this.appliedFilters.length == 0) {
      filters = [];
      filters.push({
        jsonPath: "processStatusLkp",
        value: this.READY_FOR_PAYMENT,
        operator: "Equal"
      }, {
        jsonPath: "businessStatus",
        value: this.GOOD_TO_PAY,
        operator: "Equal"
      });
    }

    this.searchInvoices(filters, undefined, undefined, this.childComponent?.recordsPerPage ?? 20);
  }

  onPageLoad(event: any) {
    this.searchInvoices(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, event.rows ?? 20);
  }

  onSearch(event: any) {
    const filters: any[] = [];

    filters.push({
      jsonPath: "serviceDate",
      value: event.Timeframe,
      operator: "greaterThan"
    });

    if (event.consumer && event.consumer.trim()) {
      filters.push({ jsonPath: "consumerName", value: event.consumer.trim(), operator: "Equal" });
    }

    if (event.provider && event.provider.trim()) {
      filters.push({ jsonPath: "providerName", value: event.provider.trim(), operator: "Equal" });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({ jsonPath: "providerPplId", value: event.providerPplId.trim(), operator: "Equal" });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({ jsonPath: "consumerPplId", value: event.consumerPplId.trim(), operator: "Equal" });
    }

    if (event.status) {
      filters.push({ jsonPath: "businessStatus", value: event.status, operator: "Equal" });
    }

    if (event.processStatusLkp) {
      filters.push({ jsonPath: "processStatusLkp", value: event.processStatusLkp, operator: "Equal" });
    }

    if (event.serviceCode) {
      filters.push({ jsonPath: "serviceCode", value: event.serviceCode, operator: "Equal" });
    }

    this.appliedFilters = filters;
    this.searchInvoices(filters, undefined, undefined, this.childComponent?.recordsPerPage ?? 20);
  }

  isFilterValid(filterValues: any, searchCallbackFilterValues: any) {
    return Object.entries(filterValues)
      .filter(([_, value]) => value !== null && value !== "")
      .every(([key, value]) => {
        const mappedKey = this.keyMapping[key] || key;
        return searchCallbackFilterValues.some((filter: any) => filter.jsonPath === mappedKey);
      });
  }

  gridAction(event: any) {
    this.buttonActionEvent = event.button;
    this.selectedCheckboxes = event.selectedCheckBox;
    this.invoiceActionForm.reset();

    if (!this.isFilterValid(this.invoiceFilterChildComponent.invoiceSearchForm.getRawValue(), this.appliedFilters)) {
      const errorMessage = `Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (event.button.buttonAction === 'denyAllInvoices') {
      if (!this.invoices?.length) {
        this.errorAlertService.openModal('error', 'Error', 'There are no invoice lines to deny.');
        return;
      }
      this.openConfirmationPopup(event);
      return;
    }

    if (!event.selectedCheckBox?.length) {
      this.errorAlertService.openModal('error', 'Error', 'Please select at least one invoice line to deny.');
      return;
    }

    this.setupModal(event.button.button_label);
  }

  openConfirmationPopup(event: any) {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      okText: 'Confirm',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
    };

    this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => this.setupModal(event.button.button_label),
      () => this.handleCancel()
    );
  }

  /**
   * Invoice line ids for the pending action. Deny uses the ticked rows; Deny All uses every row
   * currently loaded in the grid, since deny-invoice takes explicit ids rather than a search filter.
   */
  private getInvoiceLineIds(): number[] {
    const rows = this.buttonActionEvent?.buttonAction === 'denyAllInvoices'
      ? this.invoices
      : (this.selectedCheckboxes ?? []);
    return rows
      .map((row: any) => row.invoiceLineId)
      .filter((id: any) => id != null);
  }

  handleOk() {
    if (this.invoiceActionForm.invalid) {
      this.invoiceActionForm.markAllAsTouched();
      return;
    }

    const invoiceLineIds = this.getInvoiceLineIds();
    if (!invoiceLineIds.length) {
      this.errorAlertService.openModal('error', 'Error', 'No invoice lines available to deny.');
      return;
    }

    const payload = {
      invoiceLineIds,
      remarks: this.invoiceActionForm.value.remarks,
      status: this.DENIED,
    };

    this.invoiceService.denyInvoice(payload, this.programName).subscribe(() => {
      this.showActionPopup = false;
      this.loadRecordsAfterUpdate();
    });
  }

  handleCancel() {
    this.showActionPopup = false;
  }

  setupModal = (label: string) => {
    this.title = label;
    this.okText = label;
    this.showActionPopup = true;
  };

  /**
   * Reloading the grid does not reset its selection — ppl-list-table keeps the ticked rows in
   * `selectedCheckBoxValue` (the p-table `[(selection)]` target), so the just-denied rows would
   * still be in the next action's event.selectedCheckBox and get sent again.
   */
  private clearGridSelection(): void {
    this.selectedCheckboxes = [];
    if (this.childComponent?.selectedCheckBoxValue) {
      this.childComponent.selectedCheckBoxValue = [];
    }
  }
}
